import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Avatar, 
  TextField, 
  Button, 
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ReplyIcon from '@mui/icons-material/Reply';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { commentBlog, getBlogLikesAndComments, getBlogLikesAndCommentsOfUser, updateComment, deleteComment } from '../../API/API';
import LoginScreen from '../login/LoginScreen';
import RegisterUser from '../login/RegisterUser';
import OTPVerification from '../login/OTPVerification';
import WelcomeScreen from '../login/WelcomeScreen';

const CommentSectionWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(8),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -40,
    left: 0,
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.2) 50%, transparent 100%)',
  },
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  animation: 'fadeIn 0.6s ease-out',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '36px',
  fontWeight: 800,
  background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(5),
  position: 'relative',
  display: 'inline-block',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -10,
    left: -20,
    width: '40px',
    height: '40px',
    background: `radial-gradient(circle, ${theme.palette.primary.main}20 0%, transparent 70%)`,
    borderRadius: '50%',
    zIndex: -1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: '80%',
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 50%, transparent 100%)`,
    borderRadius: '4px',
  }
}));

const CommentInput = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4)
}));

const InputWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  width: '100%'
}));

const ButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(2),
  paddingRight: theme.spacing(2)
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '@keyframes dialogShow': {
    '0%': {
      opacity: 0,
      transform: 'scale(0.95) translateY(20px)',
      filter: 'blur(3px)'
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1) translateY(0)',
      filter: 'blur(0)'
    }
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(15, 23, 42, 0.65)',
    backdropFilter: 'blur(8px)',
    transition: 'all 0.3s ease-out'
  },
  '& .MuiDialog-paper': {
    borderRadius: '28px',
    padding: theme.spacing(4),
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(10px)',
    boxShadow: `
      0 20px 60px -15px rgba(0, 0, 0, 0.2),
      0 0 100px -20px rgba(25, 118, 210, 0.3)
    `,
    border: '1px solid rgba(255, 255, 255, 0.3)',
    animation: 'dialogShow 0.3s ease-out forwards',
    willChange: 'transform, opacity, filter',
    overflowY: 'visible',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '-2px',
      borderRadius: '30px',
      padding: '2px',
      background: 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 100%)',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
    }
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2, 0),
    overflowY: 'visible',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(3, 0, 0),
    marginTop: theme.spacing(2)
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '28px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(12px)',
    boxShadow: `
      0 4px 20px rgba(0, 0, 0, 0.04),
      inset 0 2px 6px rgba(255, 255, 255, 0.5),
      inset 0 -2px 6px rgba(0, 0, 0, 0.05)
    `,
    transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.98)',
      boxShadow: `
        0 8px 25px rgba(0, 0, 0, 0.08),
        inset 0 2px 6px rgba(255, 255, 255, 0.5),
        inset 0 -2px 6px rgba(0, 0, 0, 0.05)
      `,
      transform: 'translateY(-3px) scale(1.01)',
    },
    '&.Mui-focused': {
      background: '#ffffff',
      boxShadow: `
        0 12px 30px rgba(25, 118, 210, 0.15),
        inset 0 2px 6px rgba(255, 255, 255, 0.5),
        inset 0 -2px 6px rgba(0, 0, 0, 0.05),
        0 0 0 2px rgba(25, 118, 210, 0.2)
      `,
      transform: 'translateY(-5px) scale(1.02)',
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(3.5),
    fontSize: '1.15rem',
    lineHeight: 1.8,
    letterSpacing: '0.01em',
    '&::placeholder': {
      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      opacity: 0.9
    }
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '24px',
  padding: theme.spacing(2.5, 6),
  textTransform: 'none',
  fontSize: '1.15rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
  transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
    transform: 'translateX(-100%) rotate(45deg)',
    transition: 'transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  '&:hover::before': {
    transform: 'translateX(100%) rotate(45deg)',
  },
  '&.MuiButton-contained': {
    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
    boxShadow: `
      0 10px 30px -8px rgba(25, 118, 210, 0.5),
      inset 0 2px 4px rgba(255, 255, 255, 0.3),
      inset 0 -2px 4px rgba(0, 0, 0, 0.2)
    `,
    '&:hover': {
      background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
      boxShadow: `
        0 15px 35px -8px rgba(25, 118, 210, 0.6),
        inset 0 2px 4px rgba(255, 255, 255, 0.3),
        inset 0 -2px 4px rgba(0, 0, 0, 0.2)
      `,
      transform: 'translateY(-4px) scale(1.03)',
    },
    '&:active': {
      transform: 'translateY(-2px) scale(0.98)',
    },
    '&.Mui-disabled': {
      background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
      boxShadow: 'none',
    }
  },
  '&.MuiButton-text': {
    background: 'rgba(0, 0, 0, 0.04)',
    backdropFilter: 'blur(10px)',
    boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.08)',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.06)',
      transform: 'translateY(-2px)',
    }
  },
  '&.error': {
    background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
    '&:hover': {
      background: 'linear-gradient(135deg, #c62828 0%, #d32f2f 100%)',
    }
  }
}));

const InfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4.5),
  borderRadius: '28px',
  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(25, 118, 210, 0.15)',
  boxShadow: `
    inset 0 2px 6px rgba(255, 255, 255, 0.3),
    inset 0 -2px 6px rgba(0, 0, 0, 0.03),
    0 4px 20px rgba(25, 118, 210, 0.08)
  `,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, transparent 100%)',
    opacity: 0,
    transition: 'opacity 0.5s ease',
  },
  '&:hover': {
    transform: 'translateY(-3px) scale(1.01)',
    boxShadow: `
      inset 0 2px 6px rgba(255, 255, 255, 0.3),
      inset 0 -2px 6px rgba(0, 0, 0, 0.03),
      0 8px 30px rgba(25, 118, 210, 0.15)
    `,
    '&::before': {
      opacity: 1,
    }
  }
}));

const WarningBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4.5),
  borderRadius: '28px',
  background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.08) 0%, rgba(211, 47, 47, 0.03) 100%)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(211, 47, 47, 0.15)',
  boxShadow: `
    inset 0 2px 6px rgba(255, 255, 255, 0.3),
    inset 0 -2px 6px rgba(0, 0, 0, 0.03),
    0 4px 20px rgba(211, 47, 47, 0.08)
  `,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '8px',
    height: '100%',
    background: 'linear-gradient(to bottom, #d32f2f, #f44336)',
    borderRadius: '4px',
    boxShadow: '0 0 25px rgba(211, 47, 47, 0.4)',
  },
  '&:hover': {
    transform: 'translateY(-3px) scale(1.01)',
    boxShadow: `
      inset 0 2px 6px rgba(255, 255, 255, 0.3),
      inset 0 -2px 6px rgba(0, 0, 0, 0.03),
      0 8px 30px rgba(211, 47, 47, 0.15)
    `,
  }
}));

const SendButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(1, 3),
  gap: theme.spacing(1),
  transition: 'all 0.3s ease',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-2px)'
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20
  }
}));

const CommentList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  '@keyframes slideIn': {
    from: { opacity: 0, transform: 'translateX(-20px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
  },
  '& > *': {
    animation: 'slideIn 0.5s ease-out forwards',
  }
}));

const CommentCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  position: 'relative',
  padding: theme.spacing(3.5),
  background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateX(8px) translateY(-2px)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.06)',
    '& .action-buttons': {
      opacity: 1,
      transform: 'translateX(0)',
    }
  },
  '& .MuiAvatar-root': {
    width: 44,
    height: 44,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    border: '2px solid #fff',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.1) rotate(5deg)',
    }
  }
}));

const CommentContent = styled(Box)(({ theme }) => ({
  flex: 1,
  '& .comment-header': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    '& .name': {
      fontSize: '16px',
      fontWeight: 700,
      background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      letterSpacing: '-0.02em',
    },
    '& .date': {
      fontSize: '14px',
      color: '#64748B',
      position: 'relative',
      paddingLeft: theme.spacing(2),
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 8,
        top: '50%',
        width: '4px',
        height: '4px',
        background: theme.palette.primary.main,
        borderRadius: '50%',
        transform: 'translateY(-50%)',
      }
    }
  },
  '& .comment-text': {
    fontSize: '15px',
    lineHeight: 1.7,
    color: '#475569',
    marginBottom: theme.spacing(2),
    position: 'relative',
    paddingLeft: theme.spacing(3),
    '&::before': {
      content: '"\\201C"',
      position: 'absolute',
      left: 0,
      top: -8,
      fontSize: '24px',
      color: `${theme.palette.primary.main}20`,
      fontFamily: 'serif',
    }
  }
}));

const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  opacity: 0.8,
  transition: 'all 0.3s ease',
  '& .action-button': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    color: '#64748B',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: theme.spacing(0.75, 1.5),
    borderRadius: '12px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
      color: theme.palette.primary.main,
      transform: 'translateY(-2px)',
      '& svg': {
        transform: 'scale(1.2) rotate(8deg)',
      }
    },
    '& svg': {
      fontSize: 18,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    '& span': {
      transition: 'all 0.3s ease',
    },
    '&:hover span': {
      transform: 'translateX(2px)',
    }
  }
}));

const MoreButton = styled(MoreVertIcon)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(3),
  right: theme.spacing(3),
  color: '#94A3B8',
  cursor: 'pointer',
  padding: theme.spacing(0.75),
  borderRadius: '50%',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: 0,
  transform: 'translateX(10px) rotate(20deg)',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
    color: theme.palette.primary.main,
    transform: 'translateX(0) rotate(180deg)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  }
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '20px',
    padding: theme.spacing(1.5),
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(12px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: `
      0 15px 40px -10px rgba(0, 0, 0, 0.15),
      0 0 30px -5px rgba(25, 118, 210, 0.1),
      inset 0 0 0 1px rgba(255, 255, 255, 0.15),
      inset 0 0 0 2px rgba(255, 255, 255, 0.1)
    `,
    animation: 'menuFadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1) forwards',
    '@keyframes menuFadeIn': {
      '0%': {
        opacity: 0,
        transform: 'scale(0.95) translateY(-8px)',
        filter: 'blur(8px)'
      },
      '100%': {
        opacity: 1,
        transform: 'scale(1) translateY(0)',
        filter: 'blur(0)'
      }
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '-1px',
      padding: '1px',
      borderRadius: '21px',
      background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 100%)',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
    },
    '& .MuiList-root': {
      padding: theme.spacing(0.5, 0),
      overflowY: 'auto',
      maxHeight: '80vh',
      '&::-webkit-scrollbar': {
        width: '0px',
        background: 'transparent'
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'transparent'
      },
      scrollbarWidth: 'none', 
      msOverflowStyle: 'none', 
      '& > *:first-of-type': {
        marginTop: 0
      },
      '& > *:last-child': {
        marginBottom: 0
      }
    }
  }
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 2.5),
  margin: theme.spacing(0.4),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  fontSize: '1rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  minWidth: '180px',
  position: 'relative',
  overflow: 'hidden',
  background: 'transparent',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
    transform: 'translateX(-100%)',
    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    inset: '1px',
    borderRadius: '11px',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },

  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.06)',
    transform: 'translateY(-2px)',
    boxShadow: `
      0 4px 15px -3px rgba(25, 118, 210, 0.15),
      inset 0 0 0 1px rgba(25, 118, 210, 0.1)
    `,
    '&::before': {
      transform: 'translateX(100%)'
    },
    '&::after': {
      opacity: 1,
    }
  },

  '&:active': {
    transform: 'translateY(0)',
    boxShadow: `
      0 2px 8px -2px rgba(25, 118, 210, 0.15),
      inset 0 0 0 1px rgba(25, 118, 210, 0.1)
    `,
  },

  '& .MuiSvgIcon-root': {
    fontSize: '1.3rem',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  },

  '& span': {
    fontWeight: 600,
    letterSpacing: '0.01em',
    background: 'linear-gradient(135deg, #2c3e50 0%, #3f51b5 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    transition: 'opacity 0.3s ease',
  },

  '&:hover .MuiSvgIcon-root': {
    transform: 'scale(1.15) rotate(5deg)',
  },

  '&.delete-action': {
    '&:hover': {
      backgroundColor: 'rgba(211, 47, 47, 0.06)',
      boxShadow: `
        0 4px 15px -3px rgba(211, 47, 47, 0.15),
        inset 0 0 0 1px rgba(211, 47, 47, 0.1)
      `,
    },
    '&:active': {
      boxShadow: `
        0 2px 8px -2px rgba(211, 47, 47, 0.15),
        inset 0 0 0 1px rgba(211, 47, 47, 0.1)
      `,
    },
    '& span': {
      background: 'linear-gradient(135deg, #c0392b 0%, #e74c3c 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    }
  }
}));

const CommentSection = ({ blogId }) => {
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [allComments, setAllComments] = useState([]);
  const [userComments, setUserComments] = useState([]);
  const [totalComments, setTotalComments] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [menuSelectedComment, setMenuSelectedComment] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCommentText, setEditCommentText] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const user = useSelector((state) => state.user.user);
  const [localShowLoginScreen, setLocalShowLoginScreen] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showMobileOTP, setShowMobileOTP] = useState(false);
  const [showOTPVarification, setShowOTPVarification] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [requestData, setRequestData] = useState({});

  const fetchLikesAndComments = async () => {
    try {
      setIsLoading(true);
      const response = await getBlogLikesAndComments(blogId);
      if (response.result) {
        setAllComments(response.result.comments || []);
        setTotalComments(response.result.totalComments || 0);
      }

      if (user?.ID || user?.id) {
        const userResponse = await getBlogLikesAndCommentsOfUser(blogId, user?.ID || user?.id);
        if (userResponse.result) {
          setUserComments(userResponse.result.comments || []);
        }
      }
    } catch (error) {
      toast.error('Failed to load comments. Please refresh the page.', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (blogId) {
      fetchLikesAndComments();
    }
  }, [blogId]);

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;

    if (!user?.ID && !user?.id) {
      toast.error('Please login to comment on this post', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setLocalShowLoginScreen(true);
      return;
    }

    try {
      setIsSubmitting(true);
      await commentBlog(user?.ID || user?.id, blogId, commentText.trim());
      setCommentText('');
      toast.success('Comment posted successfully!', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      fetchLikesAndComments();
    } catch (error) {
      toast.error('Failed to post comment. Please try again.', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMenuClick = (event, comment) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuSelectedComment(comment);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuSelectedComment(null);
  };

  const handleEditClick = () => {
    setSelectedComment(menuSelectedComment);
    setEditCommentText(menuSelectedComment.comment);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setSelectedComment(menuSelectedComment);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleEditSubmit = async () => {
    if (!editCommentText.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    if (!selectedComment || !selectedComment.id) {
      toast.error('Comment ID not found');
      return;
    }

    try {
      setIsSubmitting(true);
      await updateComment({
        userId: user?.ID || user?.id,
        blogId,
        comment: editCommentText.trim(),
        commentId: selectedComment.id
      });
      setEditDialogOpen(false);
      setEditCommentText('');
      toast.success('Comment updated successfully!', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      fetchLikesAndComments();
    } catch (error) {
      toast.error('Failed to update comment. Please try again.', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsSubmitting(true);
      await deleteComment({
        userId: user?.ID || user?.id,
        blogId,
        comment: selectedComment.comment,
        commentId: selectedComment.id
      });
      setDeleteDialogOpen(false);
      toast.success('Comment deleted successfully!');
      fetchLikesAndComments();
    } catch (error) {
      toast.error('Failed to delete comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  return (
    <CommentSectionWrapper>
      <SectionTitle variant="h2">
        Comments {!isLoading && `(${totalComments})`}
      </SectionTitle>
      
      {user?.ID || user?.id ? (
        <CommentInput>
          <InputWrapper>
            <Avatar src="/avatars/user-avatar.jpg" alt="Your avatar" />
            <StyledTextField
              fullWidth
              multiline
              rows={2}
              placeholder="Share your thoughts..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={isSubmitting}
            />
          </InputWrapper>
          {commentText.trim() && (
            <ButtonWrapper>
              <SendButton
                variant="contained"
                onClick={handleCommentSubmit}
                disabled={isSubmitting}
                endIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </SendButton>
            </ButtonWrapper>
          )}
        </CommentInput>
      ) : (
        <InfoBox sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Please login to comment on this post.
          </Typography>
          <ActionButton variant="contained" onClick={() => setLocalShowLoginScreen(true)}>
            Login
          </ActionButton>
        </InfoBox>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : allComments.length > 0 ? (
        <CommentList>
          {allComments.map((comment, index) => {
            const isUserComment = comment.userId === (user?.ID || user?.id);
            return (
              <CommentCard 
                key={index}
                sx={{
                  backgroundColor: isUserComment ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                }}
              >
                <Avatar 
                  alt={comment.userName || "User"} 
                  src={comment.profilePhotoUrl ? comment.profilePhotoUrl : undefined}
                  sx={{
                    bgcolor: isUserComment ? 'primary.main' : 'grey.400'
                  }}
                />
                <CommentContent>
                  <Box className="comment-header">
                    <Typography 
                      className="name"
                      sx={{
                        color: isUserComment ? 'primary.main' : 'inherit',
                        fontWeight: isUserComment ? 600 : 400
                      }}
                    >
                      {isUserComment ? 'You' : (comment.userName || 'User')}
                    </Typography>
                    <Typography className="date">
                      {formatDate(comment.commitDatetime)}
                      {comment.commitDatetime !== comment.createDatetime && (
                        <span style={{ fontStyle: 'italic', color: '#888', marginLeft: 4 }}>(edited)</span>
                      )}
                    </Typography>
                  </Box>
                  <Typography className="comment-text">{comment.comment}</Typography>
                </CommentContent>
                {isUserComment && (
                  <IconButton 
                    onClick={(e) => handleMenuClick(e, comment)}
                    size="small"
                    sx={{ ml: 'auto' }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </CommentCard>
            );
          })}
        </CommentList>
      ) : (
        <Box textAlign="center" my={4}>
          <Typography color="textSecondary">
            No comments yet. Be the first to comment!
          </Typography>
        </Box>
      )}

      <StyledMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        elevation={0}
        transitionDuration={250}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              overflow: 'visible', 
              '& .MuiList-root': {
                py: 0.5
              }
            }
          }
        }}
      >
        <StyledMenuItem onClick={handleEditClick}>
          <EditIcon sx={{ 
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 2px 4px rgba(25, 118, 210, 0.2))'
          }} />
          <span>Edit Comment</span>
        </StyledMenuItem>
        <StyledMenuItem 
          onClick={handleDeleteClick} 
          className="delete-action"
        >
          <DeleteIcon sx={{ 
            background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 2px 4px rgba(211, 47, 47, 0.2))'
          }} />
          <span>Delete Comment</span>
        </StyledMenuItem>
      </StyledMenu>

      <StyledDialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            maxHeight: 'calc(100% - 64px)'
          }
        }}
      >
        <DialogTitle>
          <EditIcon sx={{ 
            fontSize: 32,
            filter: 'drop-shadow(0 4px 12px rgba(25, 118, 210, 0.3))',
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }} />
          Edit Comment
        </DialogTitle>
        <DialogContent>
          <InfoBox sx={{ mb: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 600,
                color: 'primary.main',
                mb: 1
              }}
            >
              Make your changes
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.95rem',
                lineHeight: 1.6
              }}
            >
              Update your comment below. Your changes will be visible to everyone.
            </Typography>
          </InfoBox>
          <StyledTextField
            fullWidth
            multiline
            rows={4}
            value={editCommentText}
            onChange={(e) => setEditCommentText(e.target.value)}
            placeholder="Share your thoughts here..."
          />
        </DialogContent>
        <DialogActions>
          <ActionButton onClick={() => setEditDialogOpen(false)}>
            Cancel
          </ActionButton>
          <ActionButton 
            onClick={handleEditSubmit} 
            variant="contained"
            disabled={!editCommentText.trim() || editCommentText === selectedComment?.comment || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </ActionButton>
        </DialogActions>
      </StyledDialog>

      <StyledDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: 'calc(100% - 64px)',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: `
              0 25px 80px -10px rgba(211, 47, 47, 0.25),
              0 0 40px -15px rgba(211, 47, 47, 0.15),
              inset 0 0 0 1px rgba(255, 255, 255, 0.1)
            `,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: '-2px',
              padding: '2px',
              borderRadius: '30px',
              background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.5) 0%, rgba(211, 47, 47, 0.2) 100%)',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            }
          }
        }}
        TransitionProps={{
          timeout: {
            enter: 400,
            exit: 300
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 2,
          p: 3,
          pb: 2,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(211, 47, 47, 0.2), transparent)'
          }
        }}>
          <Box sx={{ 
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: '-8px',
              background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.2) 0%, transparent 100%)',
              borderRadius: '50%',
              animation: 'pulse 2s infinite'
            },
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(0.95)',
                opacity: 0.5
              },
              '50%': {
                transform: 'scale(1.05)',
                opacity: 0.8
              },
              '100%': {
                transform: 'scale(0.95)',
                opacity: 0.5
              }
            }
          }}>
            <DeleteIcon sx={{ 
              fontSize: 32,
              background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 4px 12px rgba(211, 47, 47, 0.3))',
              animation: 'shake 0.5s cubic-bezier(.36,.07,.19,.97) both',
              '@keyframes shake': {
                '10%, 90%': {
                  transform: 'rotate(-5deg)'
                },
                '20%, 80%': {
                  transform: 'rotate(5deg)'
                },
                '30%, 50%, 70%': {
                  transform: 'rotate(-3deg)'
                },
                '40%, 60%': {
                  transform: 'rotate(3deg)'
                }
              }
            }} />
          </Box>
          <Typography variant="h6" sx={{
            background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            letterSpacing: '0.02em'
          }}>
            Delete Comment
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '24px !important'
        }}>
          <WarningBox sx={{ 
            width: '100%',
            background: 'linear-gradient(145deg, rgba(211, 47, 47, 0.08) 0%, rgba(211, 47, 47, 0.03) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(211, 47, 47, 0.15)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '6px',
              height: '100%',
              background: 'linear-gradient(to bottom, #d32f2f, #f44336)',
              borderRadius: '3px',
              boxShadow: '0 0 20px rgba(211, 47, 47, 0.4)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              right: '-50%',
              width: '200px',
              height: '200px',
              background: 'radial-gradient(circle, rgba(211, 47, 47, 0.1) 0%, transparent 70%)',
              transform: 'translateY(-50%)',
              pointerEvents: 'none'
            }
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '1.2rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&::before': {
                  content: '"⚠️"',
                  fontSize: '1.4rem'
                }
              }}
            >
              Are you sure?
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '1rem',
                lineHeight: 1.7,
                pl: 4,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  width: '20px',
                  height: '2px',
                  background: 'linear-gradient(90deg, #d32f2f, transparent)',
                  transform: 'translateY(-50%)'
                }
              }}
            >
              This action cannot be undone. The comment will be permanently deleted.
            </Typography>
          </WarningBox>
        </DialogContent>
        <DialogActions sx={{ 
          padding: theme => theme.spacing(3),
          background: 'linear-gradient(180deg, transparent, rgba(211, 47, 47, 0.03))',
          gap: 2,
          position: 'relative',
          mt: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(211, 47, 47, 0.1), transparent)'
          }
        }}>
          <ActionButton 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              background: 'rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(10px)',
              color: 'text.secondary',
              fontWeight: 600,
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.08)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Cancel
          </ActionButton>
          <ActionButton 
            onClick={handleDeleteConfirm} 
            variant="contained"
            className="error"
            sx={{
              background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
              boxShadow: `
                0 10px 30px -8px rgba(211, 47, 47, 0.5),
                inset 0 2px 4px rgba(255, 255, 255, 0.3),
                inset 0 -2px 4px rgba(0, 0, 0, 0.2)
              `,
              '&:hover': {
                background: 'linear-gradient(135deg, #c62828 0%, #d32f2f 100%)',
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: `
                  0 15px 35px -8px rgba(211, 47, 47, 0.6),
                  inset 0 2px 4px rgba(255, 255, 255, 0.3),
                  inset 0 -2px 4px rgba(0, 0, 0, 0.2)
                `
              },
              '&:active': {
                transform: 'translateY(-1px) scale(0.98)'
              }
            }}
          >
            Delete Comment
          </ActionButton>
        </DialogActions>
      </StyledDialog>

      {localShowLoginScreen && (
        <LoginScreen
          showLoginScreen={localShowLoginScreen}
          setShowLoginScreen={setLocalShowLoginScreen}
          setShowRegister={setShowRegister}
          setShowOTPVarification={setShowOTPVarification}
          setShowMobileOTP={setShowMobileOTP}
          setRequestData={setRequestData}
          requestData={requestData}
        />
      )}
      {showRegister && (
        <RegisterUser
          showRegister={showRegister}
          setShowRegister={setShowRegister}
          setShowLoginScreen={setLocalShowLoginScreen}
          setShowOTPVarification={setShowOTPVarification}
          setRequestData={setRequestData}
          requestData={requestData}
          setShowMobileOTP={setShowMobileOTP}
        />
      )}
      {showOTPVarification && (
        <OTPVerification
          showOTPVarification={showOTPVarification}
          setShowOTPVarification={setShowOTPVarification}
          showMobileOTP={showMobileOTP}
          setShowWelcome={setShowWelcome}
          setRequestData={setRequestData}
          requestData={requestData}
          redirectToApplyLoan={false}
        />
      )}
      {showWelcome && (
        <WelcomeScreen
          showWelcome={showWelcome}
          setShowWelcome={setShowWelcome}
          requestData={requestData}
          redirectToApplyLoan={false}
        />
      )}
    </CommentSectionWrapper>
  );
};

export default CommentSection; 